import { collection, query, where, getDocs, getDoc, doc, addDoc, updateDoc, orderBy, startAfter, limit, DocumentData, QueryDocumentSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { InventoryItem, Category, TransactionType } from '../utils/types';
import { Order, ShippingCompany, Review, ShippingRegion, OrderStatus, PaymentStatus, OrderItem, PaymentMethod } from '../types/store';
import { getStoreSettings } from './storeSettingsService';
import { updateInventoryQuantityInWarehouse, addInventoryTransaction, getInventoryQuantityInWarehouse } from './inventoryService';

// الحصول على المنتجات المعروضة في المتجر
export const getStoreProducts = async (
  categoryId?: string,
  lastVisible?: QueryDocumentSnapshot<DocumentData>,
  _itemsPerPage: number = 12
) => {
  try {
    let productsQuery;
    console.log('Getting store products with categoryId:', categoryId);

    // الاستعلام الأساسي
    const collectionRef = collection(db, 'inventory');

    // استراتيجية مبسطة لتجنب مشاكل الفهارس المركبة
    if (categoryId) {
      try {
        // التحقق من نوع الفئة (رئيسية أو فرعية)
        const categoryDoc = await getDoc(doc(db, 'categories', categoryId));

        if (categoryDoc.exists()) {
          const categoryData = categoryDoc.data();
          console.log('Category data:', categoryData);
          const isSubcategory = categoryData.parentId !== undefined && categoryData.parentId !== null;

          if (isSubcategory) {
            // فلترة حسب الفئة الفرعية فقط، بدون showInStore في الاستعلام
            console.log('Filtering by subcategory:', categoryId);
            productsQuery = query(
              collectionRef,
              where('subcategoryId', '==', categoryId)
            );
            console.log('Subcategory query created');
          } else {
            // فلترة حسب الفئة الرئيسية فقط، بدون showInStore في الاستعلام
            console.log('Filtering by main category:', categoryId);
            productsQuery = query(
              collectionRef,
              where('categoryId', '==', categoryId)
            );
            console.log('Main category query created');
          }
        } else {
          // إذا لم يتم العثور على الفئة، نستخدم الاستعلام الافتراضي
          console.log('Category not found, using default query');
          productsQuery = query(
            collectionRef,
            where('showInStore', '==', true),
            orderBy('createdAt', 'desc')
          );
          console.log('Default query created');
        }
      } catch (categoryError) {
        console.error('Error checking category:', categoryError);
        // في حالة حدوث خطأ أثناء التحقق من الفئة، نستخدم الاستعلام الافتراضي
        console.log('Using fallback query after error');
        productsQuery = query(
          collectionRef,
          where('showInStore', '==', true),
          orderBy('createdAt', 'desc')
        );
        console.log('Fallback query created');
      }
    } else {
      // إذا لم يتم تحديد فئة، نعرض جميع المنتجات (بدون فلتر showInStore في الاستعلام)
      console.log('No category specified, showing all products');
      productsQuery = query(
        collectionRef,
        orderBy('createdAt', 'desc'),
        limit(50) // نحصل على عدد أكبر للفلترة المحلية
      );
      console.log('All products query created');
    }

    // إضافة الصفحة التالية إذا كانت موجودة
    if (lastVisible) {
      productsQuery = query(
        productsQuery,
        startAfter(lastVisible)
      );
    }

    console.log('Executing products query...');
    const productsSnapshot = await getDocs(productsQuery);
    console.log(`Found ${productsSnapshot.size} products`);

    const lastVisibleDoc = productsSnapshot.docs[productsSnapshot.docs.length - 1];

    const products = productsSnapshot.docs
      .map(doc => {
        const data = doc.data() as InventoryItem;
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt as string),
          updatedAt: data.updatedAt instanceof Date ? data.updatedAt : new Date(data.updatedAt as string)
        };
      })
      .filter(product => {
        // فلترة محلية للمنتجات المعروضة في المتجر
        const showInStore = product.showInStore !== undefined ? product.showInStore : true;
        return showInStore === true;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); // ترتيب محلي

    console.log('Processed products:', products.length);

    return { products, lastVisible: lastVisibleDoc };
  } catch (error) {
    console.error('Error getting store products:', error);
    throw error;
  }
};

// الحصول على منتج واحد بواسطة المعرف
export const getProductById = async (productId: string) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    console.log('Fetching product with ID:', productId);
    const productDoc = await getDoc(doc(db, 'inventory', productId));

    if (!productDoc.exists()) {
      console.warn(`Product with ID ${productId} not found`);
      throw new Error('Product not found');
    }

    const productData = productDoc.data() as InventoryItem;

    // التحقق من وجود البيانات الأساسية
    if (!productData) {
      console.warn(`Product data for ID ${productId} is empty`);
      throw new Error('Product data is empty');
    }

    // التحقق من وجود حقول التاريخ
    const createdAt = productData.createdAt ?
      (productData.createdAt instanceof Date ? productData.createdAt : new Date(productData.createdAt as any)) :
      new Date();

    const updatedAt = productData.updatedAt ?
      (productData.updatedAt instanceof Date ? productData.updatedAt : new Date(productData.updatedAt as any)) :
      new Date();

    return {
      ...productData,
      id: productDoc.id,
      createdAt,
      updatedAt
    };
  } catch (error) {
    console.error('Error getting product by ID:', error);
    throw error;
  }
};

// الحصول على الفئات
export const getCategories = async (): Promise<Category[]> => {
  try {
    console.log('Starting to fetch categories');
    const categoriesRef = collection(db, 'categories');

    // تبسيط الاستعلام للتأكد من عدم وجود مشاكل
    const categoriesQuery = query(categoriesRef);

    console.log('Executing categories query');
    const categoriesSnapshot = await getDocs(categoriesQuery);
    console.log(`Found ${categoriesSnapshot.size} categories`);

    const categories: Category[] = categoriesSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || 'فئة بدون اسم',
        description: data.description || '',
        parentId: data.parentId || undefined,
        imageUrl: data.imageUrl || undefined,
        isActive: data.isActive !== undefined ? data.isActive : true,
        order: data.order || 0,
        createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
        updatedAt: data.updatedAt ? new Date(data.updatedAt.seconds * 1000) : new Date(),
        // إضافة حقل لتخزين الفئات الفرعية
        subCategories: []
      };
    });

    // تنظيم الفئات في هيكل شجري
    const mainCategories: Category[] = [];
    const subCategoriesMap: Record<string, Category[]> = {};

    // فصل الفئات الرئيسية والفرعية
    categories.forEach(category => {
      if (!category.parentId) {
        // هذه فئة رئيسية
        mainCategories.push(category);
      } else {
        // هذه فئة فرعية
        if (!subCategoriesMap[category.parentId]) {
          subCategoriesMap[category.parentId] = [];
        }
        subCategoriesMap[category.parentId].push(category);
      }
    });

    // إضافة الفئات الفرعية إلى الفئات الرئيسية
    mainCategories.forEach(mainCategory => {
      if (subCategoriesMap[mainCategory.id]) {
        mainCategory.subCategories = subCategoriesMap[mainCategory.id];
      }
    });

    console.log('Processed main categories:', mainCategories);
    return mainCategories;
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

// حذف القيم undefined من الكائن بشكل عودي لتجنب رفض Firestore لها
const removeUndefined = (obj: any): any => {
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, removeUndefined(v)])
  );
};

// إنشاء طلب جديد
export const createOrder = async (order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    // توليد رقم الطلب مسبقاً بناءً على الوقت لتجنب الحاجة إلى updateDoc منفصل
    // (العملاء لديهم صلاحية create فقط وليس write/update)
    const timestamp = Date.now();
    const orderNumber = `ORD-${timestamp.toString(36).toUpperCase().slice(-6)}`;

    const orderData = removeUndefined({
      ...order,
      orderNumber,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    const orderRef = await addDoc(collection(db, 'orders'), orderData);

    return {
      ...orderData,
      id: orderRef.id,
      orderNumber
    };
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

// الحصول على طلبات العميل
export const getCustomerOrders = async (customerId: string) => {
  try {
    console.log('Fetching orders for customer ID:', customerId);

    // استخدام استعلام بسيط بدون ترتيب لتجنب الحاجة إلى فهرس مركب
    const ordersQuery = query(
      collection(db, 'orders'),
      where('customerId', '==', customerId)
    );

    console.log('Executing orders query...');
    const ordersSnapshot = await getDocs(ordersQuery);
    console.log(`Found ${ordersSnapshot.size} orders for customer ${customerId}`);

    if (ordersSnapshot.empty) {
      console.log('No orders found for this customer');
      return [];
    }

    const orders = ordersSnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('Processing order:', doc.id);

      try {
        // معالجة حقول التاريخ بشكل أفضل
        let createdAt: Date;

        if (data.createdAt) {
          // التحقق من نوع البيانات المخزنة
          if (data.createdAt instanceof Date) {
            createdAt = data.createdAt;
          } else if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
            // Firestore Timestamp
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt.seconds && data.createdAt.nanoseconds) {
            // Firestore Timestamp في شكل كائن
            createdAt = new Date(data.createdAt.seconds * 1000 + data.createdAt.nanoseconds / 1000000);
          } else if (typeof data.createdAt === 'string') {
            // سلسلة نصية
            createdAt = new Date(data.createdAt);
          } else if (typeof data.createdAt === 'number') {
            // طابع زمني بالميلي ثانية
            createdAt = new Date(data.createdAt);
          } else {
            // إذا لم نتمكن من تحديد النوع، استخدم التاريخ الحالي
            console.warn(`Unable to parse createdAt for order ${doc.id}, using current date`);
            createdAt = new Date();
          }
        } else {
          // إذا لم يكن هناك تاريخ، استخدم التاريخ الحالي
          createdAt = new Date();
        }

        // نفس الشيء بالنسبة لتاريخ التحديث
        let updatedAt: Date;

        if (data.updatedAt) {
          if (data.updatedAt instanceof Date) {
            updatedAt = data.updatedAt;
          } else if (data.updatedAt.toDate && typeof data.updatedAt.toDate === 'function') {
            updatedAt = data.updatedAt.toDate();
          } else if (data.updatedAt.seconds && data.updatedAt.nanoseconds) {
            updatedAt = new Date(data.updatedAt.seconds * 1000 + data.updatedAt.nanoseconds / 1000000);
          } else if (typeof data.updatedAt === 'string') {
            updatedAt = new Date(data.updatedAt);
          } else if (typeof data.updatedAt === 'number') {
            updatedAt = new Date(data.updatedAt);
          } else {
            updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
          }
        } else {
          updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
        }

        // التحقق من صحة التاريخ
        if (isNaN(createdAt.getTime())) {
          console.warn(`Invalid createdAt date for order ${doc.id}, using current date`);
          createdAt = new Date();
        }

        if (isNaN(updatedAt.getTime())) {
          console.warn(`Invalid updatedAt date for order ${doc.id}, using createdAt date`);
          updatedAt = createdAt;
        }

        return {
          ...data,
          id: doc.id,
          createdAt,
          updatedAt
        };
      } catch (err) {
        console.error('Error processing order data:', err);
        // إرجاع البيانات الأساسية في حالة حدوث خطأ
        return {
          ...data,
          id: doc.id,
          createdAt: new Date(),
          updatedAt: new Date()
        };
      }
    });

    // ترتيب الطلبات بعد استرجاعها من قاعدة البيانات
    return orders.sort((a, b) => {
      // التحقق من وجود حقل createdAt
      if (!a.createdAt || !b.createdAt) return 0;

      // ترتيب تنازلي (الأحدث أولاً)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  } catch (error) {
    console.error('Error getting customer orders:', error);
    // بدلاً من رمي الخطأ، نعيد مصفوفة فارغة
    return [];
  }
};

// الحصول على طلب واحد بواسطة المعرف - تم تحديثها بوظيفة أكثر تفصيلاً أدناه

// الحصول على طلب بواسطة رقم الطلب
export const getOrderByNumber = async (orderNumber: string) => {
  try {
    const ordersQuery = query(
      collection(db, 'orders'),
      where('orderNumber', '==', orderNumber)
    );

    const ordersSnapshot = await getDocs(ordersQuery);

    if (ordersSnapshot.empty) {
      throw new Error('Order not found');
    }

    const orderDoc = ordersSnapshot.docs[0];
    const data = orderDoc.data();

    try {
      // معالجة حقول التاريخ بشكل أفضل
      let createdAt: Date;

      if (data.createdAt) {
        // التحقق من نوع البيانات المخزنة
        if (data.createdAt instanceof Date) {
          createdAt = data.createdAt;
        } else if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
          // Firestore Timestamp
          createdAt = data.createdAt.toDate();
        } else if (data.createdAt.seconds && data.createdAt.nanoseconds) {
          // Firestore Timestamp في شكل كائن
          createdAt = new Date(data.createdAt.seconds * 1000 + data.createdAt.nanoseconds / 1000000);
        } else if (typeof data.createdAt === 'string') {
          // سلسلة نصية
          createdAt = new Date(data.createdAt);
        } else if (typeof data.createdAt === 'number') {
          // طابع زمني بالميلي ثانية
          createdAt = new Date(data.createdAt);
        } else {
          // إذا لم نتمكن من تحديد النوع، استخدم التاريخ الحالي
          console.warn(`Unable to parse createdAt for order ${orderDoc.id}, using current date`);
          createdAt = new Date();
        }
      } else {
        // إذا لم يكن هناك تاريخ، استخدم التاريخ الحالي
        createdAt = new Date();
      }

      // نفس الشيء بالنسبة لتاريخ التحديث
      let updatedAt: Date;

      if (data.updatedAt) {
        if (data.updatedAt instanceof Date) {
          updatedAt = data.updatedAt;
        } else if (data.updatedAt.toDate && typeof data.updatedAt.toDate === 'function') {
          updatedAt = data.updatedAt.toDate();
        } else if (data.updatedAt.seconds && data.updatedAt.nanoseconds) {
          updatedAt = new Date(data.updatedAt.seconds * 1000 + data.updatedAt.nanoseconds / 1000000);
        } else if (typeof data.updatedAt === 'string') {
          updatedAt = new Date(data.updatedAt);
        } else if (typeof data.updatedAt === 'number') {
          updatedAt = new Date(data.updatedAt);
        } else {
          updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
        }
      } else {
        updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
      }

      // التحقق من صحة التاريخ
      if (isNaN(createdAt.getTime())) {
        console.warn(`Invalid createdAt date for order ${orderDoc.id}, using current date`);
        createdAt = new Date();
      }

      if (isNaN(updatedAt.getTime())) {
        console.warn(`Invalid updatedAt date for order ${orderDoc.id}, using createdAt date`);
        updatedAt = createdAt;
      }

      return {
        ...data,
        id: orderDoc.id,
        createdAt,
        updatedAt
      } as Order;
    } catch (err) {
      console.error('Error processing order data:', err);
      // إرجاع البيانات الأساسية في حالة حدوث خطأ
      return {
        ...data,
        id: orderDoc.id,
        createdAt: new Date(),
        updatedAt: new Date()
      } as Order;
    }
  } catch (error) {
    console.error('Error getting order by number:', error);
    throw error;
  }
};

// الحصول على شركات الشحن
export const getShippingCompanies = async () => {
  try {
    console.log('Getting shipping companies from store settings...');

    // الحصول على إعدادات المتجر
    const storeSettings = await getStoreSettings();
    console.log('Store settings loaded:', storeSettings);

    // التحقق من وجود إعدادات الشحن
    if (!storeSettings || !storeSettings.shipping) {
      console.error('Shipping settings not found in store settings');
      return [];
    }

    // طباعة إعدادات الشحن للتصحيح
    console.log('Shipping settings:', storeSettings.shipping);
    console.log('Shipping methods:', storeSettings.shipping.shippingMethods);
    console.log('Shipping regions:', storeSettings.shipping.shippingRegions);

    // التحقق من تفعيل الشحن
    if (!storeSettings.shipping.enableShipping) {
      console.log('Shipping is disabled in store settings');
      return [];
    }

    // التحقق من وجود طرق شحن
    if (!storeSettings.shipping.shippingMethods || storeSettings.shipping.shippingMethods.length === 0) {
      console.error('No shipping methods found in store settings');
      return [];
    }

    // تحويل طرق الشحن إلى شركات شحن
    const shippingCompanies: ShippingCompany[] = storeSettings.shipping.shippingMethods
      .filter(method => method.isActive)
      .map(method => {
        console.log('Processing shipping method:', method);

        // استخدام المناطق والمدن المعرفة خصيصاً لهذه الشركة، أو استخدام المناطق الافتراضية العامة
        const methodRegions = (method.regions && method.regions.length > 0) 
          ? method.regions 
          : storeSettings.shipping.shippingRegions;

        // التحقق من وجود مناطق شحن
        if (!methodRegions || methodRegions.length === 0) {
          console.warn('No shipping regions found for method:', method.name);
          // إنشاء منطقة افتراضية
          const defaultRegion: ShippingRegion = {
            id: 'default',
            name: 'منطقة افتراضية',
            cost: method.cost,
            estimatedDeliveryDays: method.estimatedDeliveryDays,
            isActive: true,
            cities: []
          };

          return {
            id: method.id,
            name: method.name,
            logo: method.icon,
            regions: [defaultRegion],
            isActive: method.isActive
          } as ShippingCompany;
        }

        // تحويل مناطق الشحن والمدن التابعة لها
        const regions: ShippingRegion[] = methodRegions
          .filter(region => region.isActive)
          .map(region => {
            console.log('Processing shipping region:', region);

            // التحقق من وجود مدن
            if (!region.cities || region.cities.length === 0) {
              console.warn('No cities found for region:', region.name);
              return {
                id: region.id,
                name: region.name,
                cost: method.cost, // استخدام تكلفة طريقة الشحن الافتراضية
                estimatedDeliveryDays: method.estimatedDeliveryDays,
                isActive: true,
                cities: []
              };
            }

            // استخدام تكلفة المدينة الأولى أو تكلفة طريقة الشحن مع تضمين جميع المدن
            return {
              id: region.id,
              name: region.name,
              cost: region.cities[0]?.shippingCost || method.cost,
              estimatedDeliveryDays: region.cities[0]?.estimatedDeliveryDays || method.estimatedDeliveryDays,
              isActive: true,
              cities: region.cities.map(city => ({
                id: city.id,
                name: city.name,
                shippingCost: city.shippingCost,
                estimatedDeliveryDays: city.estimatedDeliveryDays,
                isActive: city.isActive
              }))
            };
          });

        return {
          id: method.id,
          name: method.name,
          logo: method.icon,
          regions,
          isActive: method.isActive
        } as ShippingCompany;
      });

    console.log('Converted shipping methods to companies:', shippingCompanies);

    // Add pickup option if enabled
    if (storeSettings.shipping?.enablePickup && storeSettings.shipping?.pickupLocations?.length > 0) {
      const activePickupLocations = storeSettings.shipping.pickupLocations.filter(loc => loc.isActive);
      if (activePickupLocations.length > 0) {
        const pickupCompany: ShippingCompany = {
          id: 'pickup',
          name: 'استلام من المتجر',
          logo: '',
          regions: [
            {
              id: 'pickup_main',
              name: activePickupLocations[0].name,
              cost: 0,
              estimatedDeliveryDays: 0,
              isActive: true
            }
          ],
          isActive: true
        };
        shippingCompanies.push(pickupCompany);
      }
    }

    // Add Bosta as a shipping option if enabled
    if (storeSettings.bosta?.enabled) {
      const bostaCompany: ShippingCompany = {
        id: 'bosta',
        name: 'Bosta Express',
        logo: '',
        regions: [
          {
            id: 'bosta_dynamic',
            name: 'ديناميكي (حسب السعر من Bosta)',
            cost: 0,
            estimatedDeliveryDays: 3,
            isActive: true
          }
        ],
        isActive: true
      };
      shippingCompanies.push(bostaCompany);
    }

    return shippingCompanies;
  } catch (error) {
    console.error('Error getting shipping companies from store settings:', error);

    // في حالة الخطأ، حاول الحصول على شركات الشحن من مجموعة shipping
    try {
      console.log('Falling back to shipping collection...');
      const shippingQuery = query(
        collection(db, 'shipping'),
        where('isActive', '==', true)
      );

      const shippingSnapshot = await getDocs(shippingQuery);

      const shippingCompanies = shippingSnapshot.docs.map(doc => {
        const data = doc.data() as ShippingCompany;
        return {
          ...data,
          id: doc.id
        };
      });

      return shippingCompanies;
    } catch (fallbackError) {
      console.error('Error getting shipping companies from collection:', fallbackError);
      return [];
    }
  }
};

// إضافة مراجعة للمنتج
export const addProductReview = async (review: Omit<Review, 'id' | 'createdAt' | 'isApproved'>) => {
  try {
    const reviewData = {
      ...review,
      createdAt: new Date(),
      isApproved: false // المراجعات تحتاج إلى موافقة قبل نشرها
    };

    const reviewRef = await addDoc(collection(db, 'reviews'), reviewData);

    return {
      ...reviewData,
      id: reviewRef.id
    };
  } catch (error) {
    console.error('Error adding product review:', error);
    throw error;
  }
};

// الحصول على مراجعات المنتج
export const getProductReviews = async (productId: string) => {
  try {
    if (!productId) {
      console.warn('Product ID is required for fetching reviews');
      return [];
    }

    console.log('Fetching reviews for product ID:', productId);

    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('productId', '==', productId),
      where('isApproved', '==', true),
      orderBy('createdAt', 'desc')
    );

    const reviewsSnapshot = await getDocs(reviewsQuery);
    console.log(`Found ${reviewsSnapshot.size} reviews for product ${productId}`);

    const reviews = reviewsSnapshot.docs.map(doc => {
      const data = doc.data() as Review;

      // التحقق من وجود حقل التاريخ
      const createdAt = data.createdAt ?
        (data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt as any)) :
        new Date();

      return {
        ...data,
        id: doc.id,
        createdAt
      };
    });

    return reviews;
  } catch (error) {
    console.error('Error getting product reviews:', error);
    // بدلاً من رمي الخطأ، نعيد مصفوفة فارغة
    return [];
  }
};

// الحصول على جميع الطلبات
export const getAllOrders = async (): Promise<Order[]> => {
  try {
    console.log('Fetching all orders');

    const ordersQuery = query(
      collection(db, 'orders'),
      orderBy('createdAt', 'desc')
    );

    const ordersSnapshot = await getDocs(ordersQuery);
    console.log(`Found ${ordersSnapshot.size} orders`);

    const orders = ordersSnapshot.docs.map(doc => {
      const data = doc.data();

      try {
        // معالجة حقول التاريخ بشكل أفضل
        let createdAt: Date;

        if (data.createdAt) {
          // التحقق من نوع البيانات المخزنة
          if (data.createdAt instanceof Date) {
            createdAt = data.createdAt;
          } else if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
            // Firestore Timestamp
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt.seconds && data.createdAt.nanoseconds) {
            // Firestore Timestamp في شكل كائن
            createdAt = new Date(data.createdAt.seconds * 1000 + data.createdAt.nanoseconds / 1000000);
          } else if (typeof data.createdAt === 'string') {
            // سلسلة نصية
            createdAt = new Date(data.createdAt);
          } else if (typeof data.createdAt === 'number') {
            // طابع زمني بالميلي ثانية
            createdAt = new Date(data.createdAt);
          } else {
            // إذا لم نتمكن من تحديد النوع، استخدم التاريخ الحالي
            console.warn(`Unable to parse createdAt for order ${doc.id}, using current date`);
            createdAt = new Date();
          }
        } else {
          // إذا لم يكن هناك تاريخ، استخدم التاريخ الحالي
          createdAt = new Date();
        }

        // نفس الشيء بالنسبة لتاريخ التحديث
        let updatedAt: Date;

        if (data.updatedAt) {
          if (data.updatedAt instanceof Date) {
            updatedAt = data.updatedAt;
          } else if (data.updatedAt.toDate && typeof data.updatedAt.toDate === 'function') {
            updatedAt = data.updatedAt.toDate();
          } else if (data.updatedAt.seconds && data.updatedAt.nanoseconds) {
            updatedAt = new Date(data.updatedAt.seconds * 1000 + data.updatedAt.nanoseconds / 1000000);
          } else if (typeof data.updatedAt === 'string') {
            updatedAt = new Date(data.updatedAt);
          } else if (typeof data.updatedAt === 'number') {
            updatedAt = new Date(data.updatedAt);
          } else {
            updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
          }
        } else {
          updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
        }

        // التحقق من صحة التاريخ
        if (isNaN(createdAt.getTime())) {
          console.warn(`Invalid createdAt date for order ${doc.id}, using current date`);
          createdAt = new Date();
        }

        if (isNaN(updatedAt.getTime())) {
          console.warn(`Invalid updatedAt date for order ${doc.id}, using createdAt date`);
          updatedAt = createdAt;
        }

        // تحويل البيانات إلى نوع Order
        return {
          ...data,
          id: doc.id,
          orderNumber: data.orderNumber || `ORD-${doc.id.substring(0, 6).toUpperCase()}`,
          customerId: data.customerId || '',
          customerName: data.customerName || '',
          customerPhone: data.customerPhone || '',
          customerEmail: data.customerEmail || '',
          items: Array.isArray(data.items) ? data.items : [],
          subtotal: data.subtotal || 0,
          shippingCost: data.shippingCost || 0,
          discount: data.discount || 0,
          tax: data.tax || 0,
          total: data.total || 0,
          paymentMethod: (data.paymentMethod as PaymentMethod) || PaymentMethod.CASH_ON_DELIVERY,
          paymentStatus: (data.paymentStatus as PaymentStatus) || PaymentStatus.PENDING,
          orderStatus: (data.orderStatus as OrderStatus) || OrderStatus.PENDING,
          shippingDetails: data.shippingDetails || {
            address: '',
            city: '',
            region: '',
            phone: '',
            shippingMethod: '',
            shippingCost: 0
          },
          notes: data.notes || '',
          createdAt,
          updatedAt
        } as Order;
      } catch (err) {
        console.error('Error processing order data:', err);
        // إرجاع كائن Order افتراضي في حالة حدوث خطأ
        return {
          id: doc.id,
          orderNumber: `ORD-${doc.id.substring(0, 6).toUpperCase()}`,
          customerId: '',
          customerName: '',
          customerPhone: '',
          customerEmail: '',
          items: [],
          subtotal: 0,
          shippingCost: 0,
          discount: 0,
          tax: 0,
          total: 0,
          paymentMethod: PaymentMethod.CASH_ON_DELIVERY,
          paymentStatus: PaymentStatus.PENDING,
          orderStatus: OrderStatus.PENDING,
          shippingDetails: {
            address: '',
            city: '',
            region: '',
            phone: '',
            shippingMethod: '',
            shippingCost: 0
          },
          notes: '',
          createdAt: new Date(),
          updatedAt: new Date()
        };
      }
    });

    return orders as Order[];
  } catch (error) {
    console.error('Error getting all orders:', error);
    return [];
  }
};

// تحديث حالة الطلب
export const updateOrderStatus = async (orderId: string, orderStatus: OrderStatus) => {
  try {
    console.log(`Updating order ${orderId} status to ${orderStatus}`);

    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      orderStatus,
      updatedAt: new Date()
    });

    return true;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// تحديث حالة الدفع
export const updateOrderPaymentStatus = async (orderId: string, paymentStatus: PaymentStatus) => {
  try {
    console.log(`Updating order ${orderId} payment status to ${paymentStatus}`);

    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      paymentStatus,
      updatedAt: new Date()
    });

    return true;
  } catch (error) {
    console.error('Error updating order payment status:', error);
    throw error;
  }
};

/**
 * تسجيل دفع طلب المتجر الإلكتروني (محسّن)
 * يتم استدعاؤها عند تسليم الطلب للعميل واستلام الدفع
 */
export const recordStoreOrderPayment = async (
  orderId: string,
  amount: number,
  paymentMethod: 'cash' | 'card' | 'bankTransfer' | 'vodafone_wallet' | 'instapay' | 'etisalat_wallet',
  employeeId: string,
  employeeName: string,
  walletNumber?: string,
  notes?: string
): Promise<void> => {
  try {
    // جلب بيانات الطلب
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      throw new Error('الطلب غير موجود');
    }

    const orderData = orderDoc.data();

    // التحقق من أن الطلب لم يتم دفعه مسبقاً
    if (orderData.paymentStatus === PaymentStatus.PAID) {
      throw new Error('هذا الطلب مدفوع بالفعل');
    }

    const items = orderData.items || [];
    const warehouseId = items[0]?.warehouseId || 'default';

    // ✅ التحقق من توفر جميع المنتجات دفعة واحدة (محسّن)
    const inventoryChecks = await Promise.all(
      items.map((item: any) =>
        getInventoryQuantityInWarehouse(item.productId, item.warehouseId || 'default')
          .then((available: number) => ({ item, available }))
      )
    );

    // التحقق من النتائج
    const insufficientItems = inventoryChecks.filter(
      ({ item, available }: any) => available < item.quantity
    );

    if (insufficientItems.length > 0) {
      const errors = insufficientItems.map(
        ({ item, available }: any) =>
          `${item.productName}: متوفر ${available} من ${item.quantity}`
      ).join(', ');
      throw new Error(`كمية غير كافية: ${errors}`);
    }

    // ✅ خصم المنتجات من المخزون بشكل متوازي (محسّن)
    await Promise.all(
      items.map(async (item: any) => {
        const itemWarehouseId = item.warehouseId || 'default';

        await updateInventoryQuantityInWarehouse(
          item.productId,
          itemWarehouseId,
          -item.quantity,
          employeeId,
          `طلب متجر #${orderData.orderNumber}`
        );

        return addInventoryTransaction({
          id: '',
          type: TransactionType.SALE,
          itemId: item.productId,
          itemName: item.productName,
          itemSku: item.productSku || '',
          quantity: item.quantity,
          fromWarehouseId: itemWarehouseId,
          fromWarehouseName: 'المخزن الرئيسي',
          createdById: employeeId,
          createdByName: employeeName,
          notes: `متجر - ${orderData.orderNumber}`,
          timestamp: new Date()
        });
      })
    );

    // تحديث حالة الدفع
    await updateDoc(orderRef, {
      paymentStatus: PaymentStatus.PAID,
      orderStatus: OrderStatus.DELIVERED, // تحديث حالة الطلب إلى تم التسليم
      deliveredAt: new Date(),
      updatedAt: new Date()
    });

    // تسجيل الدفع في الخزينة إذا كان نقدي أو محفظة إلكترونية
    const shouldAddToTreasury = paymentMethod === 'cash' ||
                                paymentMethod === 'vodafone_wallet' ||
                                paymentMethod === 'instapay' ||
                                paymentMethod === 'etisalat_wallet';

    if (shouldAddToTreasury) {
      // إنشاء وصف الدفعة
      let description = `دفع طلب متجر - ${orderData.orderNumber}`;
      if (walletNumber && paymentMethod !== 'cash') {
        const walletLabel =
          paymentMethod === 'vodafone_wallet' ? 'فودافون كاش' :
          paymentMethod === 'instapay' ? 'انستاباي' :
          'اتصالات كاش';
        description += ` - ${walletLabel} (${walletNumber})`;
      }
      if (notes) {
        description += ` - ${notes}`;
      }

      await addDoc(collection(db, 'drawerTransactions'), {
        type: 'store_payment',
        amount: amount, // موجب - يزيد الخزينة
        description,
        orderId,
        orderNumber: orderData.orderNumber,
        customerId: orderData.customerId || '',
        customerName: orderData.customerName || 'عميل غير محدد',
        employeeId,
        employeeName,
        paymentMethod,
        walletNumber: walletNumber || '',
        branchId: 'default', // يمكن تحسين هذا لاحقاً
        date: new Date().toISOString().split('T')[0],
        paymentDate: new Date(),
        createdAt: serverTimestamp()
      });

      console.log(`✅ تم تسجيل دفع طلب المتجر في الخزينة: ${amount} جنيه`);
    }

    console.log(`✅ تم تسجيل دفع الطلب ${orderId} بنجاح`);
  } catch (error) {
    console.error('❌ خطأ في تسجيل دفع طلب المتجر:', error);
    throw error;
  }
};

/**
 * إرجاع طلب من المتجر الإلكتروني (محسّن)
 * يعيد المنتجات للمخزون ويخصم المبلغ من الخزينة أو يضيف رصيد للعميل
 */
export const returnStoreOrder = async (
  orderId: string,
  refundMethod: 'cash' | 'credit',
  employeeId: string,
  employeeName: string,
  returnReason: string,
  notes?: string
): Promise<void> => {
  try {
    // جلب بيانات الطلب
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      throw new Error('الطلب غير موجود');
    }

    const orderData = orderDoc.data();

    // التحقق من أن الطلب مدفوع ويمكن إرجاعه
    if (orderData.paymentStatus !== PaymentStatus.PAID) {
      throw new Error('لا يمكن إرجاع طلب غير مدفوع');
    }

    if (orderData.orderStatus === OrderStatus.CANCELLED) {
      throw new Error('هذا الطلب ملغي بالفعل');
    }

    const items = orderData.items || [];
    const totalAmount = orderData.total || 0;

    // ✅ إعادة المنتجات للمخزون بشكل متوازي (محسّن)
    await Promise.all(
      items.map(async (item: any) => {
        const warehouseId = item.warehouseId || 'default';

        await updateInventoryQuantityInWarehouse(
          item.productId,
          warehouseId,
          item.quantity,
          employeeId,
          `إرجاع متجر #${orderData.orderNumber}`
        );

        return addInventoryTransaction({
          id: '',
          type: TransactionType.ADJUSTMENT,
          itemId: item.productId,
          itemName: item.productName,
          itemSku: item.productSku || '',
          quantity: item.quantity,
          toWarehouseId: warehouseId,
          toWarehouseName: 'المخزن الرئيسي',
          createdById: employeeId,
          createdByName: employeeName,
          notes: `إرجاع متجر - ${returnReason}`,
          timestamp: new Date()
        });
      })
    );

    // ✅ معالجة الاسترداد المالي
    if (refundMethod === 'cash') {
      // خصم من الخزينة
      console.log('💰 خصم المبلغ من الخزينة...');

      let description = `إرجاع طلب متجر - ${orderData.orderNumber}`;
      if (returnReason) {
        description += ` - السبب: ${returnReason}`;
      }
      if (notes) {
        description += ` - ${notes}`;
      }

      await addDoc(collection(db, 'drawerTransactions'), {
        type: 'store_return',
        amount: -Math.abs(totalAmount), // سالب = خصم من الخزينة
        description,
        orderId,
        orderNumber: orderData.orderNumber,
        customerId: orderData.customerId || '',
        customerName: orderData.customerName || 'عميل غير محدد',
        employeeId,
        employeeName,
        branchId: 'default',
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      });

      console.log(`✅ تم خصم ${totalAmount} جنيه من الخزينة`);
    } else if (refundMethod === 'credit' && orderData.customerId) {
      // إضافة رصيد للعميل
      console.log('💳 إضافة رصيد للعميل...');

      // يمكن إضافة دالة لإضافة رصيد العميل هنا إذا كان النظام يدعم ذلك
      // await addCustomerCredit(orderData.customerId, totalAmount, ...);

      console.log(`✅ تم إضافة ${totalAmount} جنيه كرصيد للعميل`);
    }

    // تحديث حالة الطلب
    await updateDoc(orderRef, {
      orderStatus: OrderStatus.CANCELLED,
      paymentStatus: PaymentStatus.REFUNDED,
      returnReason,
      returnNotes: notes || '',
      returnedAt: new Date(),
      returnedBy: employeeId,
      returnedByName: employeeName,
      refundMethod,
      updatedAt: new Date()
    });

    console.log(`✅ تم إرجاع الطلب ${orderId} بنجاح`);
  } catch (error) {
    console.error('❌ خطأ في إرجاع طلب المتجر:', error);
    throw error;
  }
};


// تحديث عناصر الطلب
export const updateOrderItems = async (orderId: string, items: OrderItem[], total: number) => {
  try {
    console.log(`Updating order ${orderId} items`);

    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      items,
      total,
      updatedAt: new Date()
    });

    return true;
  } catch (error) {
    console.error('Error updating order items:', error);
    throw error;
  }
};

// الحصول على طلب محدد بواسطة المعرف
export const getOrderById = async (orderId: string): Promise<Order> => {
  try {
    console.log(`Fetching order with ID: ${orderId}`);

    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      console.error(`Order with ID ${orderId} not found`);
      throw new Error('Order not found');
    }

    const data = orderDoc.data();

    try {
      // معالجة حقول التاريخ بشكل أفضل
      let createdAt: Date;

      if (data.createdAt) {
        // التحقق من نوع البيانات المخزنة
        if (data.createdAt instanceof Date) {
          createdAt = data.createdAt;
        } else if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
          // Firestore Timestamp
          createdAt = data.createdAt.toDate();
        } else if (data.createdAt.seconds && data.createdAt.nanoseconds) {
          // Firestore Timestamp في شكل كائن
          createdAt = new Date(data.createdAt.seconds * 1000 + data.createdAt.nanoseconds / 1000000);
        } else if (typeof data.createdAt === 'string') {
          // سلسلة نصية
          createdAt = new Date(data.createdAt);
        } else if (typeof data.createdAt === 'number') {
          // طابع زمني بالميلي ثانية
          createdAt = new Date(data.createdAt);
        } else {
          // إذا لم نتمكن من تحديد النوع، استخدم التاريخ الحالي
          console.warn(`Unable to parse createdAt for order ${orderDoc.id}, using current date`);
          createdAt = new Date();
        }
      } else {
        // إذا لم يكن هناك تاريخ، استخدم التاريخ الحالي
        createdAt = new Date();
      }

      // نفس الشيء بالنسبة لتاريخ التحديث
      let updatedAt: Date;

      if (data.updatedAt) {
        if (data.updatedAt instanceof Date) {
          updatedAt = data.updatedAt;
        } else if (data.updatedAt.toDate && typeof data.updatedAt.toDate === 'function') {
          updatedAt = data.updatedAt.toDate();
        } else if (data.updatedAt.seconds && data.updatedAt.nanoseconds) {
          updatedAt = new Date(data.updatedAt.seconds * 1000 + data.updatedAt.nanoseconds / 1000000);
        } else if (typeof data.updatedAt === 'string') {
          updatedAt = new Date(data.updatedAt);
        } else if (typeof data.updatedAt === 'number') {
          updatedAt = new Date(data.updatedAt);
        } else {
          updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
        }
      } else {
        updatedAt = createdAt; // استخدم تاريخ الإنشاء كبديل
      }

      // التحقق من صحة التاريخ
      if (isNaN(createdAt.getTime())) {
        console.warn(`Invalid createdAt date for order ${orderDoc.id}, using current date`);
        createdAt = new Date();
      }

      if (isNaN(updatedAt.getTime())) {
        console.warn(`Invalid updatedAt date for order ${orderDoc.id}, using createdAt date`);
        updatedAt = createdAt;
      }

      // تحويل البيانات إلى نوع Order
      return {
        ...data,
        id: orderDoc.id,
        orderNumber: data.orderNumber || `ORD-${orderDoc.id.substring(0, 6).toUpperCase()}`,
        customerId: data.customerId || '',
        customerName: data.customerName || '',
        customerPhone: data.customerPhone || '',
        customerEmail: data.customerEmail || '',
        items: Array.isArray(data.items) ? data.items : [],
        subtotal: data.subtotal || 0,
        shippingCost: data.shippingCost || 0,
        discount: data.discount || 0,
        tax: data.tax || 0,
        total: data.total || 0,
        paymentMethod: (data.paymentMethod as PaymentMethod) || PaymentMethod.CASH_ON_DELIVERY,
        paymentStatus: (data.paymentStatus as PaymentStatus) || PaymentStatus.PENDING,
        orderStatus: (data.orderStatus as OrderStatus) || OrderStatus.PENDING,
        shippingDetails: data.shippingDetails || {
          address: '',
          city: '',
          region: '',
          phone: '',
          shippingMethod: '',
          shippingCost: 0
        },
        notes: data.notes || '',
        cancellationReason: data.cancellationReason || '',
        createdAt,
        updatedAt
      } as Order;
    } catch (err) {
      console.error('Error processing order data:', err);
      throw new Error('Error processing order data');
    }
  } catch (error) {
    console.error('Error getting order by ID:', error);
    throw error;
  }
};

// إلغاء الطلب من قبل العميل
export const cancelOrder = async (orderId: string, cancellationReason: string): Promise<boolean> => {
  try {
    console.log(`Cancelling order ${orderId} with reason: ${cancellationReason}`);

    // التحقق من حالة الطلب قبل الإلغاء
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      console.error(`Order with ID ${orderId} not found`);
      throw new Error('Order not found');
    }

    const orderData = orderDoc.data();

    // التحقق مما إذا كان الطلب قابلاً للإلغاء
    if (orderData.orderStatus === OrderStatus.SHIPPED ||
        orderData.orderStatus === OrderStatus.DELIVERED) {
      console.error(`Cannot cancel order ${orderId} because it is already ${orderData.orderStatus}`);
      throw new Error(`لا يمكن إلغاء الطلب لأنه ${orderData.orderStatus === OrderStatus.SHIPPED ? 'قيد الشحن' : 'تم توصيله'} بالفعل`);
    }

    // تحديث حالة الطلب إلى ملغي
    await updateDoc(orderRef, {
      orderStatus: OrderStatus.CANCELLED,
      cancellationReason,
      updatedAt: new Date()
    });

    // إذا كان الطلب مدفوعًا، قم بتحديث حالة الدفع إلى مسترد
    if (orderData.paymentStatus === PaymentStatus.PAID) {
      await updateDoc(orderRef, {
        paymentStatus: PaymentStatus.REFUNDED
      });
    }

    console.log(`Order ${orderId} cancelled successfully`);
    return true;
  } catch (error) {
    console.error('Error cancelling order:', error);
    throw error;
  }
};
